// An order's journey: ordered, picked, built, packed, shipped. Read from
// GET /orders/:id/journey. The stage words are the order list's own words where
// they overlap. A line under the strip names any place where the status and the
// numbers disagree, so a "ready" order that is not built cannot pass quietly.
import React from 'react';
import Alert from '../components/ui/Alert';
import StepStrip from '../components/ui/StepStrip';
import type { StepState } from '../components/ui/StepStrip';
import type { Journey, JourneyKey, JourneyStage } from './shipping';

interface Step {
  key: JourneyKey;
  label: string;
  detail: string | null;
  state: StepState;
}

const WORD: Record<JourneyKey, string> = {
  ordered: 'Ordered',
  picked: 'Picked',
  built: 'Built',
  packed: 'Packed',
  shipped: 'Shipped',
};

function labelFor(s: JourneyStage): string {
  if (s.key === 'built' && s.total) return `Built ${s.n ?? 0} of ${s.total}`;
  if (s.key === 'shipped' && !s.done && s.total && s.shipped) return `Shipped ${s.shipped} of ${s.total}`;
  return WORD[s.key] ?? s.key;
}

/** First stage not done is the current one; everything after it is not yet. A stage
 *  left undone behind a done one (an order shipped before packing was recorded) is
 *  not current: the order has moved past it. */
export function buildSteps(journey: Journey): Step[] {
  const lastDone = journey.stages.map((s) => s.done).lastIndexOf(true);
  let currentTaken = false;
  // No linked run means the built count is only the from-stock balance; say so.
  const noRun = journey.stages.some((s) => s.key === 'picked' && s.detail === 'No linked run');
  return journey.stages.map((s, i) => {
    let state: StepState = 'done';
    if (!s.done && i < lastDone) {
      state = 'future';
    } else if (!s.done) {
      state = currentTaken ? 'future' : 'current';
      currentTaken = true;
    }
    const detail = s.detail || (s.key === 'built' && noRun && s.total ? 'From stock' : null);
    return { key: s.key, label: labelFor(s), detail, state };
  });
}

const JourneyStrip: React.FC<{ journey: Journey }> = ({ journey }) => (
  <StepStrip label="Order journey" steps={buildSteps(journey)}>
    {journey.mismatch ? (
      <Alert variant="note" tone="warning" live="status">{journey.mismatch}</Alert>
    ) : null}
  </StepStrip>
);

export default JourneyStrip;
